import React, { useEffect, useMemo, useRef, useState } from 'react';
import './AdminTable.css';

function isEditableTarget(target) {
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName;
  return target.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
}

/**
 * @param {{
 *   children?: React.ReactNode,
 *   className?: string,
 *   enableKeyboardNav?: boolean,
 *   rowIds?: string[],
 *   selectedRowId?: string | null,
 *   onRowSelect?: ((rowId: string | null) => void) | null,
 *   onRowActivate?: ((rowId: string) => void) | null
 * }} props
 */
function AdminTable({
  children,
  className = '',
  enableKeyboardNav = false,
  rowIds = [],
  selectedRowId = undefined,
  onRowSelect = null,
  onRowActivate = null,
}) {
  const wrapRef = useRef(null);
  const normalizedRowIds = useMemo(
    () => rowIds.filter((rowId) => typeof rowId === 'string' && rowId),
    [rowIds],
  );
  const [internalSelectedRowId, setInternalSelectedRowId] = useState(normalizedRowIds[0] || null);
  const resolvedSelectedRowId = selectedRowId === undefined ? internalSelectedRowId : selectedRowId;

  useEffect(() => {
    if (!enableKeyboardNav) {
      return;
    }

    if (!normalizedRowIds.length) {
      if (selectedRowId === undefined) {
        setInternalSelectedRowId(null);
      }
      return;
    }

    if (!resolvedSelectedRowId || !normalizedRowIds.includes(resolvedSelectedRowId)) {
      const nextRowId = normalizedRowIds[0];
      if (selectedRowId === undefined) {
        setInternalSelectedRowId(nextRowId);
      }
      onRowSelect?.(nextRowId);
    }
  }, [enableKeyboardNav, normalizedRowIds, onRowSelect, resolvedSelectedRowId, selectedRowId]);

  useEffect(() => {
    if (!enableKeyboardNav || !wrapRef.current) {
      return undefined;
    }

    const rows = wrapRef.current.querySelectorAll('[data-admin-row-id]');
    rows.forEach((row) => {
      const isSelected = row.getAttribute('data-admin-row-id') === resolvedSelectedRowId;
      row.toggleAttribute('data-admin-row-selected', isSelected);
      row.classList.toggle('is-key-selected', isSelected);
      if (isSelected) {
        row.scrollIntoView({ block: 'nearest' });
      }
    });

    return undefined;
  }, [enableKeyboardNav, resolvedSelectedRowId]);

  useEffect(() => {
    if (!enableKeyboardNav || !normalizedRowIds.length) {
      return undefined;
    }

    const setSelected = (nextRowId) => {
      if (selectedRowId === undefined) {
        setInternalSelectedRowId(nextRowId);
      }
      onRowSelect?.(nextRowId);
    };

    const handleKeyDown = (event) => {
      if (event.defaultPrevented || event.altKey || event.metaKey || event.ctrlKey || isEditableTarget(event.target)) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key !== 'j' && key !== 'k' && event.key !== 'Enter') {
        return;
      }

      const currentIndex = resolvedSelectedRowId ? normalizedRowIds.indexOf(resolvedSelectedRowId) : -1;

      if (key === 'j' || key === 'k') {
        event.preventDefault();
        const direction = key === 'j' ? 1 : -1;
        const baseIndex = currentIndex >= 0 ? currentIndex : (direction > 0 ? -1 : 0);
        const nextIndex = Math.min(normalizedRowIds.length - 1, Math.max(0, baseIndex + direction));
        setSelected(normalizedRowIds[nextIndex]);
        return;
      }

      if (event.key === 'Enter' && resolvedSelectedRowId) {
        event.preventDefault();
        onRowActivate?.(resolvedSelectedRowId);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboardNav, normalizedRowIds, onRowActivate, onRowSelect, resolvedSelectedRowId, selectedRowId]);

  return (
    <div
      ref={wrapRef}
      className={`au-table-wrap au-scope${enableKeyboardNav ? ' au-table-wrap--keyboard-nav' : ''}${className ? ` ${className}` : ''}`}
    >
      <table className="au-table">{children}</table>
    </div>
  );
}

export default AdminTable;
